import { z } from 'zod';
import { CardType, PaymentRail } from '../../../payment.types';
import { StoredMaskedPanSchema } from '../../masked_pan.schemas';
import { JsonValueSchema } from '../../common.schemas';
import {
    type CardPointeCardSecureBrowserTokenizationSession,
    CardPointeTokenizationResultType,
    CardPointeBrowserTokenizationSessionType,
    type CardPointeCardSecureBrowserTokenizationResult,
    type CardPointeCardSecureBrowserTokenizationResultV2,
    type CardPointeCardSecureHostedIframeTokenizationResult,
    type CardPointeCardSecureHostedIframeTokenizationResultV2,
    type CardPointeCardSecureTokenizationSuccessResponse,
    type CardPointeCreditCardTokenizedCardMetadata,
    type CardPointeDebitCardTokenizedCardMetadata,
    type CardPointeStoredCardMetadataTokenizationResult,
    type CardPointeHostedIframeSuccessResponse,
    type CardPointeTokenizationResult,
    type CardPointeTokenizedCardMetadata,
} from '../../../types/ipg/cardpointe/cardpointe-tokenization.types';

export const CardPointeTokenizationTokenSchema: z.ZodString = z.string().min(1);

export const CardPointeCardSecureBrowserTokenizationSessionSchema: z.ZodObject<{
    browser_tokenization_session_type: z.ZodLiteral<
        typeof CardPointeBrowserTokenizationSessionType
    >;
    card_secure_base_url: z.ZodString;
}> = z
    .object({
        browser_tokenization_session_type: z.literal(CardPointeBrowserTokenizationSessionType),
        card_secure_base_url: z.string().url(),
    })
    .strict() satisfies z.ZodType<CardPointeCardSecureBrowserTokenizationSession>;

export const CardPointeCardSecureTokenizationSuccessResponseSchema: z.ZodType<CardPointeCardSecureTokenizationSuccessResponse> =
    z
        .object({
            message: z.literal('No error'),
            errorcode: z.literal(0),
            token: CardPointeTokenizationTokenSchema,
            expiry: z
                .string()
                .regex(/^\d{4}$/)
                .optional(),
        })
        .catchall(JsonValueSchema);

export const CardPointeHostedIframeSuccessResponseSchema: z.ZodType<CardPointeHostedIframeSuccessResponse> =
    z.union([
        z
            .object({
                token: CardPointeTokenizationTokenSchema,
                expiry: z.string().regex(/^\d{4}(?:[1-9]|1[0-2])$/),
                errorCode: z.union([z.literal('0'), z.literal(0)]),
                errorMessage: z.string().optional(),
            })
            .catchall(JsonValueSchema),
        z
            .object({
                message: CardPointeTokenizationTokenSchema,
                expiry: z.string().regex(/^\d{4}(?:[1-9]|1[0-2])$/),
            })
            .catchall(JsonValueSchema),
    ]);

export const CardPointeCreditCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.CREDIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodUnion<
        readonly [z.ZodLiteral<typeof CardType.CREDIT>, z.ZodLiteral<typeof CardType.UNKNOWN>]
    >;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        payment_rail: z.literal(PaymentRail.CREDIT_CARD),
        masked_pan: StoredMaskedPanSchema,
        card_type: z.union([z.literal(CardType.CREDIT), z.literal(CardType.UNKNOWN)]),
        card_brand: z.string().min(1),
        cardholder_name: z.string().min(1),
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
    })
    .strict() satisfies z.ZodType<CardPointeCreditCardTokenizedCardMetadata>;

export const CardPointeDebitCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.DEBIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodLiteral<typeof CardType.DEBIT>;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        payment_rail: z.literal(PaymentRail.DEBIT_CARD),
        masked_pan: StoredMaskedPanSchema,
        card_type: z.literal(CardType.DEBIT),
        card_brand: z.string().min(1),
        cardholder_name: z.string().min(1),
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
    })
    .strict() satisfies z.ZodType<CardPointeDebitCardTokenizedCardMetadata>;

export const CardPointeTokenizedCardMetadataSchema: z.ZodType<CardPointeTokenizedCardMetadata> =
    z.discriminatedUnion('payment_rail', [
        CardPointeCreditCardTokenizedCardMetadataSchema,
        CardPointeDebitCardTokenizedCardMetadataSchema,
    ]);

export const CardPointeCardSecureBrowserTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION
    >;
    cardsecure_response: typeof CardPointeCardSecureTokenizationSuccessResponseSchema;
    card_metadata: z.ZodOptional<typeof CardPointeTokenizedCardMetadataSchema>;
}> = z
    .object({
        result_type: z.literal(CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION),
        cardsecure_response: CardPointeCardSecureTokenizationSuccessResponseSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema.optional(),
    })
    .strict() satisfies z.ZodType<CardPointeCardSecureBrowserTokenizationResult>;

export const CardPointeCardSecureBrowserTokenizationResultContract = {
    variants: {
        v1: {
            normalize: (value: unknown): CardPointeCardSecureBrowserTokenizationResultV2 => {
                const parsedValue =
                    CardPointeCardSecureBrowserTokenizationResultSchema.parse(value);
                return CardPointeCardSecureBrowserTokenizationResultV2Schema.parse({
                    result_type:
                        CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION_V2,
                    provider_response: parsedValue.cardsecure_response,
                    token: parsedValue.cardsecure_response.token,
                    card_metadata: parsedValue.card_metadata,
                });
            },
        },
        v2: {
            normalize: (value: unknown): CardPointeCardSecureBrowserTokenizationResultV2 =>
                CardPointeCardSecureBrowserTokenizationResultV2Schema.parse(value),
        },
    },
    writerVariant: 'v2',
} as const;

export const CardPointeCardSecureBrowserTokenizationResultV2Schema = z
    .object({
        result_type: z.literal(CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION_V2),
        provider_response: CardPointeCardSecureTokenizationSuccessResponseSchema,
        token: CardPointeTokenizationTokenSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema.optional(),
    })
    .strict()
    .superRefine((value, context) => {
        if (value.provider_response.token !== value.token) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CardPointe tokenization result does not match its provider response.',
                path: ['token'],
            });
        }
    }) satisfies z.ZodType<CardPointeCardSecureBrowserTokenizationResultV2>;

export const CardPointeCardSecureHostedIframeTokenizationResultSchema = z
    .object({
        result_type: z.literal(
            CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION
        ),
        cardsecure_response: CardPointeHostedIframeSuccessResponseSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema.optional(),
    })
    .strict() satisfies z.ZodType<CardPointeCardSecureHostedIframeTokenizationResult>;

export const CardPointeCardSecureHostedIframeTokenizationResultContract = {
    variants: {
        v1: {
            normalize: (value: unknown): CardPointeCardSecureHostedIframeTokenizationResultV2 => {
                const parsedValue =
                    CardPointeCardSecureHostedIframeTokenizationResultSchema.parse(value);
                const enhanced = 'token' in parsedValue.cardsecure_response;
                return CardPointeCardSecureHostedIframeTokenizationResultV2Schema.parse({
                    result_type:
                        CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2,
                    provider_response: parsedValue.cardsecure_response,
                    token: enhanced
                        ? parsedValue.cardsecure_response.token
                        : parsedValue.cardsecure_response.message,
                    card_metadata: parsedValue.card_metadata,
                });
            },
        },
        v2: {
            normalize: (value: unknown): CardPointeCardSecureHostedIframeTokenizationResultV2 =>
                CardPointeCardSecureHostedIframeTokenizationResultV2Schema.parse(value),
        },
    },
    writerVariant: 'v2',
} as const;

export const CardPointeCardSecureHostedIframeTokenizationResultV2Schema = z
    .object({
        result_type: z.literal(
            CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2
        ),
        provider_response: CardPointeHostedIframeSuccessResponseSchema,
        token: CardPointeTokenizationTokenSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema.optional(),
    })
    .strict()
    .superRefine((value, context) => {
        const providerToken =
            'token' in value.provider_response
                ? value.provider_response.token
                : value.provider_response.message;
        if (providerToken !== value.token) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CardPointe tokenization result does not match its provider response.',
                path: ['token'],
            });
        }
    }) satisfies z.ZodType<CardPointeCardSecureHostedIframeTokenizationResultV2>;

export const CardPointeStoredCardMetadataTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<typeof CardPointeTokenizationResultType.STORED_CARD_METADATA>;
    token: typeof CardPointeTokenizationTokenSchema;
    card_metadata: typeof CardPointeTokenizedCardMetadataSchema;
}> = z
    .object({
        result_type: z.literal(CardPointeTokenizationResultType.STORED_CARD_METADATA),
        token: CardPointeTokenizationTokenSchema,
        card_metadata: CardPointeTokenizedCardMetadataSchema,
    })
    .strict() satisfies z.ZodType<CardPointeStoredCardMetadataTokenizationResult>;

export const CardPointeTokenizationResultSchema: z.ZodType<CardPointeTokenizationResult> =
    z.discriminatedUnion('result_type', [
        CardPointeCardSecureBrowserTokenizationResultSchema,
        CardPointeCardSecureBrowserTokenizationResultV2Schema,
        CardPointeCardSecureHostedIframeTokenizationResultSchema,
        CardPointeCardSecureHostedIframeTokenizationResultV2Schema,
        CardPointeStoredCardMetadataTokenizationResultSchema,
    ]);
